import type { SubscriptionPlan } from '../../api/viewSubscriptionPlanApiService';
import {
  useViewSubscriptionPlanContent,
  formatPrice,
} from '../../hooks/useViewSubscriptionPlanContent';
import CollapsibleSection from './CollapsibleSection';
import { StatSummaryCard } from '@/shared/components/StatSummaryCard';
import { EQUIPMENT_LABELS } from '@/epics/facilityManagement/constants';
import { SUBSCRIPTION_MANAGEMENT_LABELS } from '../../constants/translations/labels';
import { EQUIPMENT_RANK_COUNT } from '../../hooks/useCreateSubscription';
import { computeFinalPrice } from '../../utils/pricing';

interface ViewSubscriptionPlanContentProps {
  plan: SubscriptionPlan | null;
  activeTabId: string;
}

interface FieldValueProps {
  label: string;
  value: string;
  testId?: string;
}

// Repeated label/value pair — every read-only field on this page (across
// all tabs) is this same shape, so it's one component instead of the same
// four-line div copied per field.
const FieldValue = ({ label, value, testId }: FieldValueProps) => (
  <div className="flex min-w-0 flex-col gap-2">
    <h2
      className="text-bodySmall font-normal text-on-surface-variant"
      data-testid={testId}
    >
      {label}
    </h2>
    <p
      className="min-w-0 break-words text-bodyLarge font-normal text-neutral-10 align-middle"
      data-testid={testId ? `${testId}-value` : undefined}
    >
      {value}
    </p>
  </div>
);

const TWO_COLUMN_GRID_STYLE = {
  gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)',
} as const;

const BILLING_CYCLE_SUFFIX: Record<SubscriptionPlan['billingCycle'], string> = {
  MONTHLY: SUBSCRIPTION_MANAGEMENT_LABELS.CYCLE_SUFFIX_MONTHLY.polish,
  QUARTERLY: SUBSCRIPTION_MANAGEMENT_LABELS.CYCLE_SUFFIX_QUARTERLY.polish,
  YEARLY: SUBSCRIPTION_MANAGEMENT_LABELS.CYCLE_SUFFIX_YEARLY.polish,
};

const ViewSubscriptionPlanContent = ({
  plan,
  activeTabId,
}: ViewSubscriptionPlanContentProps) => {
  const {
    formattedDescription,
    formattedCreatedAt,
    formattedUpdatedAt,
    formattedUpdatedBy,
    formattedPrice,
    formattedDurationType,
    formattedDuration,
    formattedBillingCycle,
    formattedBilledFor,
    formattedNumberOfUsers,
    formattedNumberOfFacilities,
    formattedNumberOfQmsDocuments,
    formattedOffer,
  } = useViewSubscriptionPlanContent(plan);

  // Basic Information Tab
  if (activeTabId === 'PodstawoweInformacje') {
    return (
      <div className="flex flex-1 min-h-0 flex-col gap-6 m-[6px]">
        <CollapsibleSection
          title="Szczegóły planu"
          testId="plan-details-title"
          ariaLabel="Szczegóły planu"
          scrollableContent
        >
          <div className="flex min-w-0 flex-col gap-2">
            <FieldValue
              label="Opis"
              value={formattedDescription}
              testId="Opis"
            />
          </div>
          <div className="grid gap-x-16 gap-y-8" style={TWO_COLUMN_GRID_STYLE}>
            <FieldValue
              label="Data utworzenia"
              value={formattedCreatedAt}
              testId="Data utworzenia"
            />
            <FieldValue
              label="Zmodyfikowane przez"
              value={formattedUpdatedBy}
              testId="Zmodyfikowane przez"
            />
            <FieldValue
              label="Zmodyfikowane przez"
              value={formattedUpdatedAt}
              testId="Zmodyfikowane przez-data"
            />
          </div>
        </CollapsibleSection>
      </div>
    );
  }

  // Pricing & Billing Tab — three collapsible cards (Duration & Billing,
  // Pricing, Offer), plus an always-visible Applied Offer summary below them
  // when the plan has one
  // outside the Offer card, so it stays visible even when Offer is
  // collapsed).
  if (activeTabId === 'CennikIRozliczenia') {
    const hasOffer = !!plan?.offer?.offerName;
    const offerFinalPrice = computeFinalPrice(
      plan?.price ?? 0,
      plan?.offer?.discountValue ?? null
    );
    const billingCycleSuffix = plan?.billingCycle
      ? BILLING_CYCLE_SUFFIX[plan.billingCycle]
      : '';

    return (
      <div className="flex flex-col gap-6 m-[6px]">
        <CollapsibleSection
          title="Okres i rozliczenia"
          testId="duration-billing-title"
        >
          <div className="grid gap-x-16 gap-y-8" style={TWO_COLUMN_GRID_STYLE}>
            <FieldValue
              label="Typ okresu"
              value={formattedDurationType}
              testId="Typ okresu"
            />
            <FieldValue
              label="Okres"
              value={formattedDuration}
              testId="Okres"
            />
            <FieldValue
              label="Cykl rozliczeniowy"
              value={formattedBillingCycle}
              testId="Cykl rozliczeniowy"
            />
          </div>
        </CollapsibleSection>

        <CollapsibleSection title="Cennik" testId="pricing-title">
          <div className="flex min-w-0 flex-col gap-2">
            <FieldValue
              label="Cena"
              value={formattedPrice ? `${formattedPrice} zł` : '—'}
              testId="Cena"
            />
            {formattedBilledFor && (
              <p className="text-bodySmall font-normal text-on-surface-variant">
                {formattedBilledFor}
              </p>
            )}
          </div>
        </CollapsibleSection>

        <CollapsibleSection title="Oferta" testId="offer-title">
          <FieldValue
            label={SUBSCRIPTION_MANAGEMENT_LABELS.FIELD_DISCOUNT.polish}
            value={formattedOffer}
            testId="Oferta"
          />
        </CollapsibleSection>

        {hasOffer && (
          <StatSummaryCard
            title={plan?.offer.offerName ?? '—'}
            badge={
              plan?.offer.discountValue != null ? (
                <span className="bg-information-40 text-white text-labelMedium rounded-full px-2 py-0.5">
                  {plan.offer.discountValue}% off
                </span>
              ) : undefined
            }
            rows={[
              [
                {
                  label: SUBSCRIPTION_MANAGEMENT_LABELS.FIELD_BASE_PRICE.polish,
                  value: `${formatPrice(plan?.price)} zł`,
                },
                {
                  label: 'Cykl rozliczeniowy',
                  value: formattedBillingCycle,
                },
                {
                  label: 'Final Price',
                  value: `${formatPrice(offerFinalPrice)} zł${billingCycleSuffix}`,
                  valueClassName: 'text-labelLarge text-information-30',
                },
              ],
            ]}
          />
        )}
      </div>
    );
  }

  // Usage Limits Tab
  if (activeTabId === 'LimityUzywania') {
    return (
      <div className="flex flex-1 min-h-0 flex-col gap-6 m-[6px]">
        <CollapsibleSection
          title="Limity użytkowania"
          testId="usage-limits-title"
          ariaLabel="Limity użytkowania"
          scrollableContent
        >
          <div className="grid gap-x-16 gap-y-8" style={TWO_COLUMN_GRID_STYLE}>
            <FieldValue
              label="Liczba użytkowników"
              value={formattedNumberOfUsers}
              testId="Liczba użytkowników"
            />
            <FieldValue
              label="Liczba placówek"
              value={formattedNumberOfFacilities}
              testId="Liczba placówek"
            />
            <FieldValue
              label="Liczba dokumentów QMS"
              value={formattedNumberOfQmsDocuments}
              testId="Liczba dokumentów QMS"
            />
          </div>
        </CollapsibleSection>
      </div>
    );
  }

  // Services Tab — the outer "Services" title is plain (no chevron), and
  // each service is its own collapsible card: title = service name, chevron
  // collapses the whole card down to just that header row ().
  if (activeTabId === 'Services') {
    return (
      <div className="flex flex-1 min-h-0 flex-col gap-6 m-[6px]">
        <div className="flex flex-1 min-h-0 flex-col gap-4 border border-[#DCDDE0] rounded-[8px] p-4">
          <p
            className="text-[18px] font-medium leading-[26px] text-on-surface-variant"
            aria-label={SUBSCRIPTION_MANAGEMENT_LABELS.STEP_SERVICES.polish}
            data-testid="services-title"
          >
            {SUBSCRIPTION_MANAGEMENT_LABELS.STEP_SERVICES.polish}
          </p>
          <div className="flex flex-1 min-h-0 flex-col gap-6 overflow-y-auto">
            {(plan?.services ?? []).map((service) => {
              const finalPrice = computeFinalPrice(
                service.basePrice,
                service.discountPercentage
              );
              return (
                <CollapsibleSection
                  key={service.id}
                  title={service.serviceName ?? '—'}
                >
                  <div
                    className="grid gap-x-16 gap-y-8"
                    style={TWO_COLUMN_GRID_STYLE}
                  >
                    <FieldValue
                      label={
                        SUBSCRIPTION_MANAGEMENT_LABELS.FIELD_SERVICE_TYPE.polish
                      }
                      value={service.serviceName ?? '—'}
                    />
                    <FieldValue
                      label={
                        SUBSCRIPTION_MANAGEMENT_LABELS.FIELD_BASE_PRICE.polish
                      }
                      value={`${formatPrice(service.basePrice)} zł`}
                    />
                    <FieldValue
                      label={
                        SUBSCRIPTION_MANAGEMENT_LABELS.FIELD_DISCOUNT_PERCENTAGE
                          .polish
                      }
                      value={
                        service.discountPercentage != null
                          ? `${service.discountPercentage}%`
                          : '—'
                      }
                    />
                    <FieldValue
                      label={
                        SUBSCRIPTION_MANAGEMENT_LABELS.FIELD_USAGE_LIMIT.polish
                      }
                      value={String(service.usageLimit)}
                    />
                  </div>
                  <StatSummaryCard
                    title={service.serviceName ?? '—'}
                    badge={
                      service.discountPercentage != null ? (
                        <span className="bg-information-40 text-white text-labelMedium rounded-full px-2 py-0.5">
                          {service.discountPercentage}% off
                        </span>
                      ) : undefined
                    }
                    rows={[
                      [
                        {
                          label:
                            SUBSCRIPTION_MANAGEMENT_LABELS.FIELD_BASE_PRICE
                              .polish,
                          value: `${formatPrice(service.basePrice)} zł`,
                        },
                        {
                          label:
                            SUBSCRIPTION_MANAGEMENT_LABELS.FIELD_USAGE_LIMIT
                              .polish,
                          value: String(service.usageLimit),
                        },
                        {
                          label: 'Final Price',
                          value: `${formatPrice(finalPrice)} zł`,
                          valueClassName: 'text-labelLarge text-information-30',
                        },
                      ],
                    ]}
                  />
                </CollapsibleSection>
              );
            })}
          </div>
        </div>
      </div>
    );
  }

  // Equipment Pricing Tab — read-only list, no per-category discount (that
  // comes from the plan's discount ladder, not a per-category override)
  if (activeTabId === 'EquipmentPricing') {
    return (
      <div className="flex flex-1 min-h-0 flex-col gap-6 m-[6px]">
        <CollapsibleSection
          title={SUBSCRIPTION_MANAGEMENT_LABELS.STEP_EQUIPMENT_PRICING.polish}
          testId="equipment-pricing-title"
          scrollableContent
        >
          <div className="flex flex-col gap-6">
            {(plan?.equipmentCategories ?? []).map((equipment) => (
              <div
                key={equipment.id}
                className="grid w-full gap-x-8 gap-y-8 border border-[#DCDDE0] rounded-[8px] p-4"
                style={TWO_COLUMN_GRID_STYLE}
              >
                <FieldValue
                  label={EQUIPMENT_LABELS.FIELD_CATEGORY}
                  value={equipment.equipmentCategoryName ?? '—'}
                />
                <FieldValue
                  label={SUBSCRIPTION_MANAGEMENT_LABELS.FIELD_BASE_PRICE.polish}
                  value={`${formatPrice(equipment.basePrice)} zł`}
                />
              </div>
            ))}
          </div>
        </CollapsibleSection>
      </div>
    );
  }

  // Discount Logic Tab — the plan's 7-position discount ladder by equipment
  // price rank (rank 7 also covers any equipment ranked 8th or later)
  if (activeTabId === 'DiscountLogic') {
    return (
      <div className="flex flex-1 min-h-0 flex-col gap-6 m-[6px]">
        <CollapsibleSection
          title={SUBSCRIPTION_MANAGEMENT_LABELS.STEP_DISCOUNT_LOGIC.polish}
          testId="discount-logic-title"
          scrollableContent
        >
          <div className="flex flex-col gap-6">
            {(plan?.discountTiers ?? [])
              .slice()
              .sort((a, b) => a.equipmentRank - b.equipmentRank)
              .map((tier) => (
                <div
                  key={tier.id}
                  className="grid w-full gap-x-8 gap-y-8 border border-[#DCDDE0] rounded-[8px] p-4"
                  style={TWO_COLUMN_GRID_STYLE}
                >
                  <FieldValue
                    label={
                      SUBSCRIPTION_MANAGEMENT_LABELS.EQUIPMENT_RANK_PREFIX
                        .polish
                    }
                    value={`${SUBSCRIPTION_MANAGEMENT_LABELS.EQUIPMENT_RANK_PREFIX.polish} ${tier.equipmentRank}${tier.equipmentRank === EQUIPMENT_RANK_COUNT ? '+' : ''}`}
                  />
                  <FieldValue
                    label={
                      SUBSCRIPTION_MANAGEMENT_LABELS
                        .FIELD_PLAN_DISCOUNT_PERCENTAGE.polish
                    }
                    value={`${tier.discountPercentage}%`}
                  />
                </div>
              ))}
          </div>
        </CollapsibleSection>
      </div>
    );
  }

  return null;
};

export default ViewSubscriptionPlanContent;
